import React, { createContext, ReactNode, useContext, useEffect } from 'react'
import { useAddAgentRoute } from './use-add-agent-route'
import {
  useAvailableAgents,
  UseAvailableAgentsResponse
} from '../api/use-available-agents'
import {
  WizardConfig,
  WizardConfigName,
  wizardConfigs,
  WizardStep
} from '../wizard-config'
import {
  useAddAgentCurrentStepIndex,
  UseAddAgentCurrentStepIndexResponse
} from './use-add-agent-current-step-index'
import {
  useAddAgentConfigurationMode,
  UseAddAgentConfigurationModeResponse
} from './use-add-agent-configuration-mode'
import { useAddAgentWizardConfig } from './use-add-agent-wizard-config'
import { useAddAgentConfigDownloadedStatus } from './use-add-agent-config-downloaded-status'
import { useOrganization } from '../../organization-licenses/api/use-organization'

const defaultConfigName = 'JAVA'
const defaultStep = 'download'

const emptyFunction = () => {
  /* intentionally empty */
}

type ContextType = Pick<
  UseAddAgentCurrentStepIndexResponse,
  | 'previousStepIndex'
  | 'currentStepIndex'
  | 'previousStep'
  | 'nextStep'
  | 'updateCurrentStepIndex'
> &
  Pick<
    UseAddAgentConfigurationModeResponse,
    | 'previousConfigurationMode'
    | 'configurationMode'
    | 'updateConfigurationMode'
  > & {
    agentsDataIsLoading: UseAvailableAgentsResponse['isLoading']
    availableAgents: UseAvailableAgentsResponse['availableAgents']
    isWrappedByProvider: boolean
    isFreemium: boolean
    isLoading: boolean
    initialRoute: string
    wizardConfig: WizardConfig
    configName: WizardConfigName
    getStepByIndex: (stepIndex: number) => WizardStep
    isOpen: boolean
    updateWizardRoute: (configName: WizardConfigName, step: WizardStep) => void
    onClose: () => void
    onDone: () => void
    organizationUuid: string
    isConfigDownloaded: boolean
    markConfigDownloadComplete: () => void
  }

const Context = createContext<ContextType>({
  isWrappedByProvider: false,
  isFreemium: false,
  isLoading: false,
  agentsDataIsLoading: false,
  availableAgents: [],
  initialRoute: '',
  configName: defaultConfigName,
  wizardConfig: wizardConfigs.JAVA,
  getStepByIndex: () => defaultStep,
  isOpen: false,
  updateWizardRoute: emptyFunction,
  onClose: emptyFunction,
  onDone: emptyFunction,
  organizationUuid: '',
  previousConfigurationMode: 'preconfigured',
  configurationMode: 'preconfigured',
  updateConfigurationMode: emptyFunction,
  isConfigDownloaded: false,
  markConfigDownloadComplete: emptyFunction,
  previousStepIndex: undefined,
  currentStepIndex: 0,
  previousStep: emptyFunction,
  nextStep: emptyFunction,
  updateCurrentStepIndex: emptyFunction
})

export const UseAddAgentWizardProvider = ({
  organizationUuid,
  getRouteHistory,
  children
}: {
  organizationUuid: string
  getRouteHistory: () => string[]
  children: ReactNode
}) => {
  const {
    isValid,
    configName: configNameFromRoute,
    step: stepFromRoute,
    goToPreviousRoute,
    goToApplicationsRoute,
    getInitialRoute,
    updateWizardRoute
  } = useAddAgentRoute({
    organizationID: organizationUuid,
    getRouteHistory,
    defaultConfigName,
    defaultStep
  })

  const { wizardConfig, updateWizardConfig } = useAddAgentWizardConfig({
    configName: configNameFromRoute
  })

  const organizationData = useOrganization({
    organizationUuid,
    expandValue: 'freemium'
  })

  const availableAgentsData = useAvailableAgents({
    organizationUuid,
    // Only enable this request if an organization was successfully retrieved
    enabled: !organizationData.isLoading && !organizationData.isError
  })

  const currentStepIndexData = useAddAgentCurrentStepIndex({
    currentStepIndex: wizardConfig.steps.indexOf(stepFromRoute),
    // Update route whe step index changes
    onUpdate: () => {
      if (isValid) {
        const newStep = getStepByIndex(currentStepIndexData.currentStepIndex)
        updateWizardRoute(configNameFromRoute, newStep)
      }
    }
  })

  const configModeData = useAddAgentConfigurationMode()

  const configDownloadStatusData = useAddAgentConfigDownloadedStatus()

  const reset = ({ currentStepIndex }: { currentStepIndex: number }) => {
    currentStepIndexData.reset(currentStepIndex)
    configModeData.reset()
    configDownloadStatusData.reset()
  }

  // Make updates when the wizard configuration changes
  useEffect(() => {
    reset({ currentStepIndex: wizardConfig.steps.indexOf(stepFromRoute) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardConfig])

  // Reset when wizard is closed
  const onClose = () => {
    updateWizardConfig('JAVA')
    reset({ currentStepIndex: 0 })
    goToPreviousRoute()
  }

  const getStepByIndex = (stepIndex: number) => wizardConfig.steps[stepIndex]

  if (organizationData.isError) {
    throw new Error('Failed getting organization data')
  }

  if (availableAgentsData.isError) {
    throw new Error('Failed getting available agents data')
  }

  return (
    <Context.Provider
      value={{
        isWrappedByProvider: true,
        organizationUuid,
        wizardConfig,
        isOpen: isValid,
        isFreemium: organizationData.data?.freemium ?? false,
        isLoading: organizationData.isLoading,
        agentsDataIsLoading: availableAgentsData.isLoading,
        availableAgents: availableAgentsData.availableAgents,
        initialRoute: getInitialRoute(
          availableAgentsData.availableAgents || []
        ),
        configName: configNameFromRoute,
        getStepByIndex,
        updateWizardRoute,
        onClose,
        onDone: goToApplicationsRoute,
        isConfigDownloaded: configDownloadStatusData.isAgentConfigDownloaded,
        markConfigDownloadComplete: () =>
          configDownloadStatusData.markAgentConfigIsDownloading(),
        ...configModeData,
        ...currentStepIndexData
      }}
    >
      {children}
    </Context.Provider>
  )
}

export const useAddAgentWizard = () => {
  const context = useContext(Context)
  if (!context.isWrappedByProvider) {
    throw new Error(
      'useAddAgentWizard must be wrapped by UseAddAgentWizardProvider'
    )
  }
  return context
}
